import { ArrowRight, Printer } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { transferMoveUrl } from '@/lib/transferQr'
import { printLabelViaIframe } from '@/lib/printLabel'
import { buildMoveLabelHtml, moveLabelContent, MOVE_LABEL_STYLE } from '@/lib/transferPrint'
import type { StorageTransferMove } from '@/types/storage'
import { itemCode, itemDetail, itemKindLabel } from './movePreview'
import { QrImage, useQrSvg } from './TransferPlanQr'

/**
 * One move's QR code, for a handset to open exactly that move.
 *
 * Drawn on demand in a dialog rather than inline on every row: a plan can be
 * ten thousand moves, and generating ten thousand codes to show three of them
 * would stall the page. Only offered on an approved plan, for the same reason
 * the plan's own code is — a draft can change under whoever walks to the
 * freezer with it.
 *
 * The item and both addresses sit beside the code so the person holding the
 * phone can check they are scanning the right row.
 */
export function TransferMoveQrDialog({
  transferId,
  move,
  sourceCode,
  targetCode,
  onClose,
}: {
  transferId: string
  move: StorageTransferMove | null
  /** The plan's two unit codes, for the printed label's "ULT-01 → ULT-02" line. */
  sourceCode: string
  targetCode: string
  onClose: () => void
}) {
  return (
    <Dialog open={move !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-w-sm'>
        {move && (
          <MoveQrBody transferId={transferId} move={move} sourceCode={sourceCode} targetCode={targetCode} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function MoveQrBody({
  transferId,
  move,
  sourceCode,
  targetCode,
}: {
  transferId: string
  move: StorageTransferMove
  sourceCode: string
  targetCode: string
}) {
  // Same-origin, as with the plan's code: a baked-in host would send a
  // staging code at production.
  const url = transferMoveUrl(window.location.origin, transferId, move.id)
  const { svg, error } = useQrSvg(url)
  const kind = itemKindLabel(move.item)
  const detail = itemDetail(move.item)

  return (
    <>
      <DialogHeader>
        <DialogTitle className='font-mono'>{itemCode(move.item)}</DialogTitle>
        <DialogDescription>
          {kind ? `${kind}${detail ? ` · ${detail}` : ''}` : 'Scan to open this move on a handset'}
        </DialogDescription>
      </DialogHeader>

      <div className='flex flex-col items-center gap-3'>
        <QrImage svg={svg} label={`QR code to open the move of ${itemCode(move.item)}`} className='h-56 w-56' />
        <p className='flex flex-wrap items-center justify-center gap-1.5 font-mono text-[13px]'>
          {move.sourceNodePath}
          <ArrowRight className='h-3.5 w-3.5 text-muted-foreground' />
          {move.targetNodePath ?? 'no destination yet'}
        </p>
        {error ? (
          <p className='text-[13px] text-destructive'>Could not draw the code: {error}</p>
        ) : (
          <p className='break-all text-center font-mono text-[11px] text-muted-foreground'>{url}</p>
        )}
        {/* Prints ONLY this move, on its own sheet — the same QR already drawn
            above, never redrawn for paper. Read-only, so offered with no
            extra permission check beyond seeing this dialog at all. */}
        <Button
          variant='outline'
          size='sm'
          disabled={!svg}
          onClick={() => {
            const content = moveLabelContent(move, sourceCode, targetCode, url)
            printLabelViaIframe(content.itemCode, buildMoveLabelHtml(content, svg ?? ''), MOVE_LABEL_STYLE)
          }}
        >
          <Printer className='mr-1.5 h-3.5 w-3.5' />
          Print label
        </Button>
      </div>
    </>
  )
}
